"use client";

import { Megaphone, ShieldCheck, TrendingUp, Trophy, Users2 } from "lucide-react";
import { useEffect, useState } from "react";

import { SportWatermark } from "@/components/branding/sport-watermark";

const CAPABILITIES = [
  {
    icon: Trophy,
    title: "One platform, every sport",
    body: "Grounds, courts, kits, tournaments and annual meets - digitised for every sport your school plays.",
  },
  {
    icon: TrendingUp,
    title: "Monetize your grounds",
    body: "Turn idle after-hours real estate into recurring revenue with a public booking marketplace.",
  },
  {
    icon: Users2,
    title: "Community & events",
    body: "Students form teams and communities; schools run inter/intra tournaments with live scoring.",
  },
  {
    icon: Megaphone,
    title: "Advertising & partnerships",
    body: "Sponsor slots on events, facilities, and the consumer marketplace - built in from day one.",
  },
  {
    icon: ShieldCheck,
    title: "Safety by design",
    body: "Role-scoped access, guarded gates, and moderated communities keep students and visitors safe.",
  },
];

const FOOTER_LINKS = ["About Founders", "Vision", "Policies", "Terms of Service", "Privacy", "Website", "Careers"];

export function BrandPanel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % CAPABILITIES.length), 4500);
    return () => clearInterval(id);
  }, []);

  const active = CAPABILITIES[index];

  return (
    <div className="relative hidden h-full flex-col justify-between overflow-hidden bg-primary p-10 text-primary-foreground lg:flex">
      <SportWatermark />

      <div className="relative z-10 flex items-center gap-2 text-xl font-bold">
        <Trophy className="h-7 w-7" />
        PlaySpace Campus
      </div>

      <div className="relative z-10 max-w-md space-y-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/70">
          Why schools choose us
        </p>
        <div key={index} className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-3">
          <div className="flex size-12 items-center justify-center rounded-xl bg-primary-foreground/10">
            <active.icon className="size-6" />
          </div>
          <h2 className="text-2xl font-bold leading-tight">{active.title}</h2>
          <p className="text-sm text-primary-foreground/80">{active.body}</p>
        </div>
        <div className="flex gap-1.5">
          {CAPABILITIES.map((_, i) => (
            <button
              key={i}
              aria-label={`Show capability ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-primary-foreground" : "w-1.5 bg-primary-foreground/30"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 flex flex-wrap gap-x-4 gap-y-1 text-xs text-primary-foreground/60">
        {FOOTER_LINKS.map((link) => (
          <a key={link} href="#" className="hover:text-primary-foreground hover:underline">
            {link}
          </a>
        ))}
      </div>
    </div>
  );
}

export function BrandAccordion() {
  return (
    <details className="group rounded-lg border bg-muted/30 lg:hidden">
      <summary className="flex cursor-pointer list-none items-center justify-between p-3 text-sm font-medium">
        About PlaySpace Campus
        <span className="text-muted-foreground transition-transform group-open:rotate-180">⌄</span>
      </summary>
      <div className="space-y-3 border-t p-3">
        {CAPABILITIES.map((c) => (
          <div key={c.title} className="flex gap-2.5">
            <c.icon className="mt-0.5 size-4 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">{c.title}</p>
              <p className="text-xs text-muted-foreground">{c.body}</p>
            </div>
          </div>
        ))}
        <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1 text-xs text-muted-foreground">
          {FOOTER_LINKS.map((link) => (
            <a key={link} href="#" className="hover:text-primary hover:underline">
              {link}
            </a>
          ))}
        </div>
      </div>
    </details>
  );
}
